import {
  KNOWN_USERS,
  KTMA_PASSPORT_TEMPLATE,
  PASSWORD,
  PIN,
  TOKENS,
  accessionAgreementBase64,
  contractTemplate,
  registeredBundle,
  templates,
  unregisteredBundle,
  widgetList,
} from './fixtures';
import {
  addPassport,
  addWidgetRecord,
  cancelWidgetRecord,
  createContract,
  getPassport,
  listPassportAttachments,
  listPassports,
  listWidgetRecords,
} from './state';

function json(data: unknown, status = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

function toPassportDocument(item: Record<string, unknown>) {
  const {
    id,
    number,
    status,
    createdAt,
    orgOwnerId,
    attachments,
    ...data
  } = item;

  return {
    id,
    templateId: KTMA_PASSPORT_TEMPLATE.id,
    alias: 'Цифровой паспорт',
    documentNumber: number == null ? undefined : String(number),
    documentVersion: 1,
    status,
    data,
    party1: orgOwnerId ? { id: orgOwnerId } : undefined,
    createdAt,
    updatedAt: createdAt,
  };
}

function readBody(init?: RequestInit): any {
  if (!init?.body) {
    return {};
  }
  if (typeof init.body === 'string') {
    try {
      return JSON.parse(init.body);
    } catch {
      return {};
    }
  }
  return {};
}

function tokensForLogin(login: string) {
  const key = KNOWN_USERS[login] ?? 'admin';
  try {
    localStorage.setItem('mock-persona', key);
  } catch {
    // ignore
  }
  return TOKENS[key];
}

export function handleRest(url: URL, init?: RequestInit): Response | null {
  const path = url.pathname.replace(/\/+$/, '');
  const method = (init?.method || 'GET').toUpperCase();
  const body = readBody(init);

  if (method === 'GET' && path.endsWith('/cert/mock/bundle/registered')) {
    return json(registeredBundle);
  }
  if (method === 'GET' && (path.endsWith('/cert/mock/bundle') || path.endsWith('/cert/mock/bundle/unregistered'))) {
    return json(unregisteredBundle);
  }
  if (method === 'POST' && path.endsWith('/cert/class')) {
    return json({ cert_class: 'qualified', CertClass: 'qualified' });
  }

  if (method === 'POST' && path.endsWith('/auth/login/pki/start')) {
    const exists = localStorage.getItem('mock-client-registered') === 'true';
    return json({
      token: 'pki-start-token',
      tbs_data: 'mock-tbs-challenge',
      tbs_type: 'string',
      identity_exists: exists,
    });
  }
  if (method === 'POST' && path.endsWith('/auth/login/pki')) {
    return json(TOKENS.admin);
  }

  if (method === 'POST' && path.endsWith('/auth/login/psw/start')) {
    const login = String(body.login || '');
    return json({ identity_exists: Boolean(KNOWN_USERS[login]) });
  }
  if (method === 'POST' && path.endsWith('/auth/login/psw')) {
    if (body.password !== PASSWORD) {
      return json({ message: 'verification failed', code: 403 }, 403);
    }
    return json(tokensForLogin(String(body.login || '')));
  }

  if (method === 'POST' && path.endsWith('/auth/openid-connect/token')) {
    return json(TOKENS.admin);
  }

  // Registration by certificate. `tbs_data` is the accession agreement the user signs on
  // the offer screen — same shape as the login challenge, so the mock signer handles it.
  if (method === 'POST' && path.endsWith('/register/signup/pki/start')) {
    return json({
      token: 'pki-signup-token',
      tbs_data: 'mock-accession-agreement',
      tbs_type: 'string',
      tbd_data: accessionAgreementBase64,
      identity_exists: false,
    });
  }
  if (method === 'POST' && path.endsWith('/register/signup/pki')) {
    return json(TOKENS.admin);
  }

  if (method === 'POST' && path.endsWith('/register/signup/psw/start')) {
    return json({ token: 'signup-token', identity_exists: false });
  }
  if (method === 'POST' && path.endsWith('/register/signup/psw')) {
    if (body.pin_code && body.pin_code !== PIN) {
      return json({ message: 'invalid pin' }, 400);
    }
    return json(TOKENS.admin);
  }

  if (method === 'POST' && path.endsWith('/auth/recovery/password/start')) {
    return json({ success: true });
  }
  if (method === 'POST' && path.endsWith('/auth/recovery/password')) {
    return json({ success: true });
  }

  // Contract form scheme. `new-contract` reads it over REST from the template's `api`
  // path: `/create` for a new document, `/{id}/sign` when signing an existing one.
  if (method === 'GET' && /\/np-supply\/(create|[^/]+\/sign)$/.test(path)) {
    return json(contractTemplate);
  }
  // Creating the contract writes it into mock state so the list/preview see the new draft.
  if (method === 'POST' && path.endsWith('/np-supply')) {
    const created = createContract((body.data ?? body) as Record<string, unknown>);
    return json({ id: created.id });
  }

  // Widget list: schema-driven table rendered by modules/dashboard/custom-widget.
  if (method === 'GET' && path.endsWith('/widget/requests/list')) {
    const list = widgetList();
    const records = [...listWidgetRecords(), ...list.records];
    return json({ ...list, records, meta: { ...list.meta, total: records.length } });
  }
  // Widget form: same steps format as contracts. `/create` for a new record,
  // `/{id}/update` when the current side may edit it, `/{id}/sign` when it must sign.
  if (method === 'GET' && /\/widget\/requests\/(create|[^/]+\/(update|sign))$/.test(path)) {
    // `isNeedToSign` is what makes the widget form show its submit button.
    return json({ ...contractTemplate, isNeedToSign: true });
  }
  if (method === 'POST' && path.endsWith('/widget/requests')) {
    const input = (body.data ?? body) as Record<string, unknown>;
    const created = createContract(input);
    addWidgetRecord({ ...input, id: created.id });
    return json({ id: created.id });
  }
  if (method === 'GET' && /\/(applications\/api\/assets|assets)\/create$/.test(path)) {
    return json({
      steps: [
        {
          name: 'Базовые параметры',
          elements: [
            {
              field: 'DocumentNumber',
              label: 'Номер договора',
              type: 'input',
              isRequired: true,
              supposedInputType: 'string',
            },
            {
              field: 'DraftingPlace',
              label: 'Место заключения договора',
              type: 'input',
              isRequired: true,
              supposedInputType: 'string',
            },
            {
              field: 'StartingDate',
              label: 'Дата заключения договора',
              type: 'datepicker',
              isRequired: true,
            },
            {
              field: 'partyId',
              label: 'Контрагент',
              type: 'input',
              isRequired: true,
              supposedInputType: 'string',
            },
            {
              field: 'DocumentValidityPeriod',
              label: 'Дата окончания действия договора',
              type: 'datepicker',
              isRequired: true,
            },
          ],
          buttons: [{ type: 'submit', label: 'Отправить' }],
        },
      ],
    });
  }
  if (method === 'POST' && /\/applications\/api\/assets$/.test(path) && !path.includes('passports')) {
    // Тип договора КТМ-А выбирается шаблоном в мастере — сохраняем его в документе,
    // иначе в карточке любой договор Активов выглядел бы как поставка топлива.
    const template = templates.find((item) => item.id === String(body.templateId ?? ''));
    const created = createContract((body.data ?? body) as Record<string, unknown>, {
      title: template?.title,
      typeId: template?.typeId,
      templateId: template?.id,
    });
    return json({ id: created.id });
  }

  if (method === 'GET' && /\/document-templates$/.test(path)) {
    const alias = url.searchParams.get('alias');
    const restTemplates = [KTMA_PASSPORT_TEMPLATE];
    return json(restTemplates.filter((item) => !alias || item.alias === alias));
  }

  if (method === 'GET' && /\/documents$/.test(path)) {
    const templateId = url.searchParams.get('templateId');
    if (templateId && templateId !== KTMA_PASSPORT_TEMPLATE.id) {
      return json({ items: [], total: 0, limit: 20, offset: 0 });
    }
    const items = listPassports({
      search: url.searchParams.get('search') ?? undefined,
      entityType: url.searchParams.get('entityType') ?? undefined,
      status: url.searchParams.get('status') ?? undefined,
      pzp: url.searchParams.get('pzp') ?? undefined,
    }).map(toPassportDocument);
    return json({ items, total: items.length, limit: 20, offset: 0 });
  }

  if (method === 'GET' && /\/documents\/([^/]+)$/.test(path)) {
    const [, id] = path.match(/\/documents\/([^/]+)$/) ?? [];
    const item = getPassport(id ?? '');
    return item
      ? json(toPassportDocument(item))
      : json({ code: 404, message: 'not found' }, 404);
  }

  if (method === 'POST' && /\/passports$/.test(path)) {
    try {
      return json(toPassportDocument(addPassport(body)), 201);
    } catch (error) {
      return json(error, (error as { code?: number }).code ?? 400);
    }
  }

  if (method === 'GET' && /\/passports\/([^/]+)\/attachments$/.test(path)) {
    const [, id] = path.match(/\/passports\/([^/]+)\/attachments$/) ?? [];
    return json(listPassportAttachments(id ?? ''));
  }

  if (method === 'POST' && /\/agreements$/.test(path)) {
    const created = createContract({
      ...(body.data ?? {}),
      partyId: body.counterpartyId,
    } as Record<string, unknown>);
    return json({ id: created.id }, 201);
  }

  if (method === 'POST' && /\/widget\/requests\/([^/]+)\/cancel$/.test(path)) {
    const [, id] = path.match(/\/widget\/requests\/([^/]+)\/cancel$/) ?? [];
    cancelWidgetRecord(id ?? '');
    return json({ success: true });
  }

  return null;
}
